import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { useQueryWithLogging } from '../useQueryWithLogging';
import { ExperimentFilters } from '@/common/ExperimentFilters/ExperimentFiltersDock/IExperimentFilters';
import {
  GetMetricQueryResponse,
  GetMetricScorecardResponse,
  GetScorecardResponse,
} from '@/features/experiment/detail/axa-scorecards-view/types';
import { RegionalExperimentConfigValues } from '@/features/experiment/domain';
import { queryKeys } from '@/hooks/constants';
import { ExperimentViewsFilters } from '@/hooks/constants/keys/experiment';
import { useMutationWithLogging } from '@/hooks/useMutationWithLogging';
import Service, { Experiment, ExperimentAnalysis } from '@/lib/App';

export function useGetExperiment(
  experimentName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.experiment(experimentName),
    queryFn: () => Service.planner.getExperiment(experimentName),
    ...options,
  });
}

export function useGetRegionalExperimentChildren(
  experimentName: string,
  countryCode: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.regionalExperimentChildren(
      experimentName,
      countryCode,
    ),
    queryFn: () =>
      Service.planner.getRegionalExperimentChildren(
        experimentName,
        countryCode,
      ),
    ...options,
  });
}

export function useGetAdaptiveExperiment(
  experimentName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.adaptiveExperiment(experimentName),
    queryFn: () => Service.planner.getAdaptiveExperiment(experimentName),
    ...options,
  });
}

export function useExperimentTags(experimentName: string) {
  return useQuery({
    queryKey: queryKeys.planner.experimentTags(experimentName),
    queryFn: () => Service.planner.fetchExperimentTags(experimentName),
  });
}

export function useExperimentEvents(experimentName: string) {
  return useQuery({
    queryKey: queryKeys.planner.experimentEvents(experimentName),
    queryFn: () => Service.planner.monitorExperiment(experimentName),
  });
}

export function useApproveExperiment(options?: { [key: string]: any }) {
  return useMutationWithLogging({
    mutationFn: ([experimentName, isAdaptiveExp, approvalLink]: [
      string,
      boolean,
      string?,
    ]) =>
      Service.planner.changeExperimentState(
        experimentName,
        'approve',
        '',
        isAdaptiveExp,
        approvalLink,
      ),
    ...options,
  });
}

export function useAbortExperiment(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (payload: {
      expName: string;
      action: string;
      reason?: string;
      isAdaptiveExp?: boolean;
      approvalLink?: string;
    }) => await Service.planner.changeExperimentStateV2(payload),
    ...options,
  });
}

export function useAbortRegionalExperiment(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (payload: {
      expName: string;
      countryCode?: string;
      action: string;
      reason?: string;
      isAdaptiveExp?: boolean;
      approvalLink?: string;
    }) => await Service.planner.changeRegionalExperimentState(payload),
    ...options,
  });
}

export function useGetExperimentAvailability(
  domain: string,
  restrictBy: string,
  overlappingDomain: boolean,
  isExpTimeSliceOrGroupStrategy: boolean,
  version?: number,
  experimentName?: string,
  options?: { [key: string]: any },
) {
  return useQuery({
    queryKey: queryKeys.planner.experimentAvailability(domain, restrictBy),
    queryFn: () =>
      Service.planner.getExperimentAvailability(
        domain,
        restrictBy,
        overlappingDomain,
        isExpTimeSliceOrGroupStrategy,
        version,
        experimentName,
      ),
    ...options,
  });
}

export function useFetchExperimentViews(
  filters: ExperimentFilters,
  pageNumber: number,
  pageSize: number,
  options?: { enabled?: boolean },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.experimentViews({
      ...filters,
      limit: pageSize,
      offset: (pageNumber - 1) * pageSize,
    }),
    queryFn: () =>
      Service.planner.fetchExperimentViewsV2(
        filters,
        pageSize,
        (pageNumber - 1) * pageSize,
      ),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useGetScorecard(
  experimentName: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging<GetScorecardResponse>({
    queryKey: queryKeys.planner.scorecard(experimentName),
    queryFn: () => Service.planner.getScorecard(experimentName),
    enabled: !!experimentName,
    ...options,
  });
}

export function useGetMetricScorecard(
  experimentName: string,
  metricId: string,
  options?: { [key: string]: any },
) {
  return useQueryWithLogging<GetMetricScorecardResponse>({
    queryKey: queryKeys.planner.metricScorecard(experimentName, metricId),
    queryFn: () => Service.planner.getMetricScorecard(experimentName, metricId),
    enabled: !!experimentName && !!metricId,
    ...options,
  });
}

export function useGetMetricQuery(
  experimentName: string,
  metricId: string,
  dimension = 'overall',
  options?: { [key: string]: any },
) {
  return useQueryWithLogging<GetMetricQueryResponse>({
    queryKey: queryKeys.planner.metricQuery(
      experimentName,
      metricId,
      dimension,
    ),
    queryFn: () =>
      Service.planner.getMetricQuery(experimentName, metricId, dimension),
    enabled: !!experimentName && !!metricId,
    ...options,
  });
}

export function useReviewExperiment(options?: { [key: string]: any }) {
  return useMutation({
    mutationFn: async (payload: {
      experiment: Experiment;
      analysis_v2?: ExperimentAnalysis;
      regional_experiment_config_values?: RegionalExperimentConfigValues[];
    }) =>
      await Service.planner.reviewExperiment(
        payload.experiment,
        payload.analysis_v2,
        payload.regional_experiment_config_values,
      ),
    ...options,
  });
}

export function useGetExperimentViews(
  filters: ExperimentViewsFilters = {},
  options?: { [key: string]: any },
) {
  return useQueryWithLogging({
    queryKey: queryKeys.planner.experimentViews(filters),
    queryFn: () => Service.planner.fetchExperimentViewsV2(filters),
    ...options,
  });
}
